/**
 * Reference docs for the sections that document VALUES and conventions rather than components.
 *
 * `componentDocs.ts` answers "which component, and what props". It cannot answer the
 * question the foundations raise, which is not "what exists" — the swatches, shadow stops
 * and size rows below each section already show that — but **"which one do I reach for,
 * and what must I never do"**. That decision was only ever in prose, spread across a
 * paragraph per section, and prose is where a rule goes to be skimmed past.
 *
 * So each foundation gets the same shape as a component: a summary, a decision table, the
 * prohibitions, and a snippet you can paste. The table is `job -> token`, deliberately not
 * `token -> value`: a reader arriving here has a job and needs the token, and the value is
 * already on the swatch.
 *
 * `iconography` and `typography` are NOT here — they are real components with real props,
 * so they live in `componentDocs.ts` with the other components.
 */

export interface FoundationChoice {
  /** The job the reader has. Phrased as what they are trying to do, not as a category. */
  job: string;
  /** The token or class that does it. */
  token: string;
  /** When this one rather than its neighbours. */
  when: string;
}

export interface FoundationDoc {
  /** The showcase section this documents. */
  section: string;
  name: string;
  summary: string;
  choose: FoundationChoice[];
  /** The prohibitions. Each one is a rule a test or a review actually enforces. */
  never: string[];
  snippet: string;
}

export const FOUNDATION_DOCS: FoundationDoc[] = [
  {
    section: 'colour',
    name: 'Colour',
    summary:
      'Colour is chosen by JOB, never by palette step. A component names what the colour is FOR — ink, a ground, an edge, a tone — and the theme decides the value, which is the whole reason dark mode works without a second set of components.',
    choose: [
      { job: 'Body text, headings, anything you read first', token: 'tw-text-ink', when: 'The default. If you are unsure, it is this.' },
      { job: 'Supporting text beside primary text', token: 'tw-text-ink-secondary', when: 'Descriptions, helper lines, the second line of a row.' },
      { job: 'Metadata nobody acts on', token: 'tw-text-ink-tertiary', when: 'Timestamps, counts, captions, column hints.' },
      { job: 'Text in a disabled control', token: 'tw-text-ink-disabled', when: 'Always — never a grey step, which inverts the hierarchy in dark.' },
      { job: 'Text on a saturated fill', token: 'tw-text-ink-inverse', when: 'On accent-bold, on a tone bold, on the inverse surface.' },
      { job: 'The ground of a card, panel or page', token: 'tw-bg-surface', when: 'The resting ground everything sits on.' },
      { job: 'A ground that recedes', token: 'tw-bg-surface-sunken', when: 'Table heads, code wells, the track under a control — a band whose host is `surface`.' },
      { job: 'A band inside a RAISED surface', token: 'tw-bg-surface-recess', when: 'Dialog and Card footers, a Menu submenu. One rung under its host in both themes; `sunken` there would be the page ground in dark.' },
      { job: 'A row or control under the cursor', token: 'tw-bg-surface-hover', when: 'Hover only. Never as a resting ground.' },
      { job: 'A decorative border or divider', token: 'tw-border-edge', when: 'Separating things. It is not required to reach 3:1.' },
      { job: 'A border that IDENTIFIES a control', token: 'tw-border-edge-strong', when: 'A field or checkbox outline — this one must clear 3:1.' },
      { job: 'Interactive ink — a link, a selected label', token: 'tw-text-accent', when: 'The resting interactive colour.' },
      { job: 'The ground of a selected row or option', token: 'tw-bg-accent-tint', when: 'The one selection grammar. Pair it with accent ink.' },
      { job: 'A filled call to action', token: 'tw-bg-accent-bold', when: 'Primary buttons. Always with ink-inverse on top.' },
      { job: 'A focus indicator you have to draw yourself', token: 'tw-ring-focus', when: 'Only where FOCUS_RING or FOCUS_RING_INSET cannot go (`tw-outline-focus` for an outline). The indicator has its own role, never `accent`, so a retune of one is not a retune of the other. Draw the ring alone: a focus-state shadow would replace the control\'s own.' },
      { job: 'Colour that carries MEANING', token: 'tw-*-tone-{tone}-{tint,ink,glyph,bold}', when: 'Status, Tag, Alert, Badge, Progress. tint is the ground, ink the text on it, glyph the mark on a plain surface, bold the saturated fill.' },
    ],
    never: [
      'Never name a palette step in a component — `tw-text-grey-900`, `tw-bg-blue-100`. A step cannot theme, so every one is a light-mode-only bug waiting for dark.',
      'Never write a raw hex. If no role fits, the role layer is missing one — add it to `tokens.css` rather than working around it.',
      'Never use a tone to decorate. A red chip must mean something is wrong, or red stops meaning anything.',
      'Never let colour be the only carrier of meaning — pair a tone with its glyph or a word (WCAG 1.4.1).',
    ],
    snippet: `<div className="tw-bg-surface tw-border tw-border-edge tw-rounded-srf tw-p-4">
  <p className="tw-text-ink">Hex Bolt M8×40</p>
  <p className="tw-text-ink-secondary">Zinc-plated, DIN 933</p>
  <span className="tw-text-ink-tertiary">Updated 2 days ago</span>
</div>`,
  },

  {
    section: 'elevation',
    name: 'Elevation & shape',
    summary:
      'How far a surface floats above the page, and how soft its corners are. The two move together: the further a plane floats, the rounder it gets — so depth reads even where the shadow is subtle.',
    choose: [
      { job: 'A resting card, or scroll-edge chrome', token: 'tw-shadow-ds-sm', when: 'The lowest real lift. Most surfaces want no shadow at all.' },
      { job: 'A bottom edge shadowing UP onto content', token: 'tw-shadow-ds-sm-up', when: 'A dialog footer with more content still below it.' },
      { job: 'A pinned table column, while it is scrolled past', token: 'tw-shadow-ds-sm-right / -sm-left', when: 'The seam becomes a layer only while cells slide under it.' },
      { job: 'A popover — Menu, a listbox, a hovered card', token: 'tw-shadow-ds-md', when: 'It floats, but it is not modal.' },
      { job: 'What floats over the page — Dialog, SidePanel, CommandPalette, Toast, SelectionBar', token: 'tw-shadow-ds-lg', when: 'Only these. A card reaching for this competes with the modal layer.' },
      { job: 'Corners on a control', token: 'tw-rounded-ctl', when: 'Inputs, buttons, chips — 4px, shared with un-migrated fields in the same toolbar.' },
      { job: 'Corners on a surface', token: 'tw-rounded-srf', when: 'Cards, menus, tables — 8px.' },
      { job: 'Corners on a floating overlay', token: 'tw-rounded-ovl', when: 'Dialog and CommandPalette — 12px. A SidePanel is anchored to the viewport edge and keeps square corners.' },
    ],
    never: [
      'Never stack two `tw-shadow-*` classes on one element. They do not compose — the last one simply wins, which is how a glow once silently replaced the shadow under it.',
      'Never use a Tailwind stock shadow. They are pure black, and black over these blue-grey neutrals reads muddy; the DS stops are tinted with the ink.',
      'Never give a card `lg`. That stop is what tells a user something is modal.',
      'Never hand-write a radius. The three names ARE the ramp; a fourth value makes the page look assembled from parts.',
    ],
    snippet: `<div className="tw-bg-surface tw-rounded-srf tw-shadow-ds-sm tw-p-4">
  A resting card
</div>

<div className="tw-bg-surface-raised tw-rounded-ovl tw-shadow-ds-lg tw-p-6">
  An overlay
</div>`,
  },

  {
    section: 'control-sizing',
    name: 'Control sizing',
    summary:
      'One height ladder shared by every control that can sit in a row — 28 / 36 / 44px. Size is DENSITY and nothing else: the type token never changes with it, so a dense table and a roomy form read at the same size.',
    choose: [
      { job: 'A dense toolbar, a table row, a config panel', token: 'size="sm"', when: '28px. Tight, and still clear of the 24px target floor.' },
      { job: 'Almost everything else', token: 'size="md"', when: '36px — the default. Forms, page toolbars, dialogs.' },
      { job: 'A primary action given room, or a touch target', token: 'size="lg"', when: '44px. The comfortable end of the ladder.' },
      { job: 'The label above a dense field', token: 'labelSize="sm"', when: 'Independent of the control height — a dense panel wants a quieter label.' },
      { job: 'A menu opened from a roomy trigger', token: 'menuVariant="compact"', when: 'Row density is independent of trigger height. A long list usually wants tighter rows.' },
    ],
    never: [
      'Never set a height by hand on a DS control. Every control in a row must resolve to the same rung, and the ladder is the only thing guaranteeing that.',
      'Never change type size to make something denser — that is what `size` is for, and shrinking the text instead breaks the reading rhythm across a screen.',
      'Never mix rungs in one row. A `sm` button beside an `md` field is the exact defect this ladder was introduced to end.',
      'Never use the long-form `small` / `medium` / `large` spelling in new code — it is deprecated, and `apiCallSites.test.ts` only lets its last legacy call sites fall.',
    ],
    snippet: `<div className="tw-flex tw-items-center tw-gap-2">
  <Search size="sm" value={q} onChange={setQ} label="Find" labelSize="sm" />
  <Dropdown size="sm" label="Status" options={statuses} value={s} onChange={setS} />
  <Button size="sm" variant="primary">Apply</Button>
</div>`,
  },

  {
    section: 'motion',
    name: 'Keyboard, focus & motion',
    summary:
      'Behaviour every component already has without a prop: a focus ring on keyboard focus only, arrow-key navigation inside composite controls, and one duration ladder with four named curves. Nothing here is decoration — each rung says how far a thing has to travel.',
    choose: [
      { job: 'A state flip that should feel instant', token: 'tw-duration-instant', when: '100ms. A checkbox, a tone swap.' },
      { job: 'Hover and press on a control', token: 'tw-duration-fast', when: '150ms. Buttons, icon buttons, menu rows.' },
      { job: 'A moving part with distance to cover', token: 'tw-duration-base', when: '200ms. The segmented-control thumb, a tab indicator.' },
      { job: 'Something the user should watch progress', token: 'tw-duration-slow', when: '300ms. A bar filling.' },
      { job: 'The default curve for arriving', token: 'tw-ease-swift', when: 'Fast arrival, long soft settle. Use this unless you have a reason.' },
      { job: 'A control that should feel physical', token: 'tw-ease-spring', when: 'Overshoots slightly. Presses and toggles — never text.' },
      { job: 'A large surface coming to rest', token: 'tw-ease-settle', when: 'Dialogs and panels, which travel far enough for the settle to read.' },
      { job: 'A loop that never lands', token: 'tw-ease-breathe', when: 'Pulses and shimmers — a live status dot, a skeleton sweep.' },
      { job: 'Any animation at all', token: 'motion-safe:', when: 'Prefix it. Under reduced-motion nothing should move.' },
    ],
    never: [
      'Never animate without `motion-safe:`. Reduced-motion is a stated accessibility need, not a preference to design around.',
      'Never write a raw duration in a component. The four rungs are the vocabulary; a fifth value makes two screens disagree about how fast the app feels.',
      'Never reach for the browser easings — plain `ease-out` is flatter than every curve here and reads as cheap beside them.',
      'Never suppress the focus ring. It appears on keyboard focus only and never on a mouse click, so there is nothing to tidy away.',
    ],
    snippet: `{/* The components carry their own motion. Yours is for what you draw beside them:
    a glyph that turns with its disclosure, exactly as Dropdown's chevron does. */}
<ChevronDown
  aria-hidden="true"
  className={\`motion-safe:tw-transition-transform motion-safe:tw-duration-base motion-safe:tw-ease-swift \${
    expanded ? 'tw-rotate-180' : ''
  }\`}
/>`,
  },
  {
    section: 'api-conventions',
    name: 'Component API',
    summary:
      'The same idea has the same name on every component, so knowing one component means knowing the rest. Older names keep working; new code writes the canonical one.',
    choose: [
      { job: 'Show or hide an overlay', token: 'open', when: 'Dialog, SidePanel, ConfirmDialog, Popover, CommandPalette, Toast, DatePicker. Never `isOpen` in new code.' },
      { job: 'Say a component is busy', token: 'loading', when: 'Button, IconButton, SidePanel, Search, Dropdown, Combobox, Table, ConfirmDialog. Never `isLoading`.' },
      { job: 'Pick a size', token: "'sm' | 'md' | 'lg'", when: '28 / 36 / 44px on the control grid — and the same three words for a glyph box.' },
      { job: 'Name a control for assistive tech', token: 'aria-label', when: 'The native attribute, on every component. `ariaLabel` is the older spelling; `aria-label` wins when both are passed.' },
      { job: 'Hide a label the layout has no room for', token: 'hideLabel', when: 'Pass the real `label` too: it is hidden and stays the accessible name. `label=""` left the control with none.' },
      { job: 'Explain a field', token: 'helperText', when: 'Text, or a node with a link. It is read out with the field.' },
      { job: 'Say what went wrong', token: 'errorMessage', when: 'Its own announced line under the hint, so the hint stays in view. Setting it puts the field in error.' },
      { job: 'Let a control keep its own value', token: 'defaultValue', when: '`defaultChecked` on Checkbox and Toggle, `defaultActiveTabId` on Tab. Without one, the control is controlled, exactly as before.' },
      { job: 'Say no tone', token: "tone: 'neutral'", when: 'The six tones are the token families: neutral, info, success, warning, danger, accent. Never `default`.' },
      { job: 'Line up with an edge', token: "'start' | 'end'", when: 'DropdownButton `align`, Popover `placement`. Never `left` / `right`.' },
      { job: 'Add classes to any component', token: 'className', when: 'Merged: a conflicting class you pass replaces the component’s own; everything else it draws is untouched.' },
    ],
    never: [
      'Never add an `is`-prefixed boolean prop. The library says `open`, `loading`, `disabled` — a new `isVisible` is a second name for an idea that already has one.',
      'Never invent a size word. `sm | md | lg` is the scale; `compact`, `small` or `xs` on a new prop makes two call sites disagree about the same box.',
      'Never override a component with `!important` (`!tw-…`). A plain class you pass already replaces the conflicting one — reach for `!` only when fighting legacy CSS, and say why.',
      'Never make a busy Button natively `disabled` yourself. `loading` blocks the click and keeps focus; `disabled` throws focus to the page.',
      'Never hide a label with `label=""`. The control is left with no name. Pass the label and `hideLabel`.',
      'Never make a card clickable with an `onClick` on the card. A keyboard cannot reach a `<div>`. Pass `action`, which names one real control.',
    ],
    snippet: `<Dialog
  open={confirmOpen}
  onClose={close}
  title="Delete rule?"
  tone="danger"
  footer={(
    <div className="tw-flex tw-justify-end tw-gap-2">
      <Button variant="secondary" onClick={close}>Cancel</Button>
      <Button variant="danger-primary" loading={deleting} onClick={remove}>Delete rule</Button>
    </div>
  )}
>
  <TextField label="Reason" value={reason} onChange={setReason}
    helperText={<>Shown in the audit log. <Link href="/docs/audit">What is logged</Link></>}
    errorMessage={reasonError} />
</Dialog>`,
  },
];

export const foundationDocFor = (sectionId: string): FoundationDoc | undefined =>
  FOUNDATION_DOCS.find((doc) => doc.section === sectionId);
